'use client';
import React, { useEffect, useRef } from 'react';

export default function FinalCTASection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.2 }
    );
    sectionRef?.current?.querySelectorAll('.reveal-up')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-32 relative overflow-hidden">
      {/* Intense ambient */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(61,255,196,0.08) 0%, rgba(180,160,255,0.06) 40%, transparent 70%)' }}
      />

      {/* Grid lines intensified */}
      <div
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{
          backgroundImage: 'linear-gradient(rgba(61,255,196,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(61,255,196,0.04) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <div className="reveal-up mb-6">
          <span className="badge-mint text-xs font-display font-700 px-3 py-1.5 rounded-full uppercase tracking-wider">
            Your turn
          </span>
        </div>

        <h2 className="reveal-up delay-100 text-[clamp(2.5rem,6vw,5rem)] font-display font-700 text-iwhite leading-[1.0] tracking-tight mb-6">
          Had a lucky moment<br />
          <span style={{ color: '#3DFFC4' }}>today?</span>
        </h2>

        <p className="reveal-up delay-200 text-xl text-white/50 font-body max-w-2xl mx-auto mb-12 leading-relaxed">
          Every lucky escape, windfall, and serendipitous encounter that goes unlogged is a data point the world loses. Thirty seconds is all it takes to add yours to the map.
        </p>

        {/* CTA cluster */}
        <div className="reveal-up delay-300 flex flex-col items-center gap-4">
          <a
            href="#"
            className="cta-primary mint-glow inline-flex items-center gap-3 px-10 py-5 rounded-full text-lg font-display font-700 group"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover:translate-x-1 transition-transform duration-200">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
            Log My Luck Incident — It&apos;s Free
          </a>
          <a href="#" className="text-sm text-white/35 hover:text-white/65 transition-colors font-body underline underline-offset-4">
            Browse the live feed first →
          </a>
          <p className="text-xs text-white/20 font-body mt-2">No account · Anonymous by default · Live in 30 seconds</p>
        </div>

        {/* Velocity tagline */}
        <div className="reveal-up delay-400 mt-20 flex items-center justify-center gap-4">
          <div className="h-px w-24 bg-gradient-to-r from-transparent to-white/10" />
          <span className="text-xs font-mono text-white/20 uppercase tracking-widest">47,382 incidents logged. Yours is next.</span>
          <div className="h-px w-24 bg-gradient-to-l from-transparent to-white/10" />
        </div>
      </div>
    </section>
  );
}